import { createRef, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useEventDownloadWaitState } from '../hooks/use-event-download-wait-state';
import { useLargeDownloads } from '../hooks/use-large-downloads';
import type { LargeDownloadStateProgressType, UseLargeDownloads } from '../types';
import { DownloadProgressCard } from './download-progress-card';

export const largeDownloadsRef = createRef<UseLargeDownloads>();

export const LargeDownloads = forwardRef((_props, ref) => {
  const root = document.getElementById('root');
  const chatbot = document.getElementById('ada-entry');
  const { downloadsWaitState, waitForDownload } = useLargeDownloads();
  const { handleClearEvent, addEventInProgress, inProgressState } = useEventDownloadWaitState();
  const { isSmallMode } = useIsSmallMode();

  useImperativeHandle(
    ref,
    () => ({
      downloadsWaitState,
      waitForDownload,
      handleClearEvent,
      addEventInProgress,
    }),
    [downloadsWaitState, handleClearEvent, waitForDownload, addEventInProgress]
  );

  const data: LargeDownloadStateProgressType = {
    ...downloadsWaitState,
    ...inProgressState,
  };

  return root
    ? createPortal(
        <div className='large-downloads-container' css={getStyles(!!chatbot, isSmallMode)}>
          {Object.entries(data).map(([key, value]) => (
            <DownloadProgressCard options={value} key={key} />
          ))}
        </div>,
        root
      )
    : null;
});

LargeDownloads.displayName = 'LargeDownloads';

const getStyles = (hasChatbot: boolean, isSmallMode: boolean) => css`
  bottom: ${theme.spacing(hasChatbot ? 7 : 1)};
  position: fixed;
  right: ${isSmallMode ? theme.spacing(1) : theme.spacing(8)};
  z-index: 10;
`;
